package org.vreo.devicehub.features.device.presentation.addDevice

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.presentation.stingInputToFloatFormatter.validateAndFormatAmount
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.device.domain.DeviceAccessRepository
import org.vreo.devicehub.features.core.domain.device.device.ProductType

/**
 * Add device view model, responsible for linking device to user's account
 *
 * @property authenticationToken user authentication token
 * @property repository repository for accessing device APIs
 */
class AddDeviceViewModel(
    val authenticationToken: String,
    val repository: DeviceAccessRepository
) : ViewModel() {
    private val _state = MutableStateFlow(AddDeviceState())
    val state = _state.asStateFlow()

    /**
     * On action handler
     *
     * @param action action to be handled
     */
    fun onAction(action: AddDeviceActions) {
        when (action) {
            is AddDeviceActions.OnClearError -> {
                _state.update {
                    it.copy(
                        error = null
                    )
                }
            }

            is AddDeviceActions.OnUpdateSelectedProduct -> {
                _state.update {
                    it.copy(
                        selectedProductIndex = action.index
                    )
                }
            }

            is AddDeviceActions.OnUpdateSerialNumber -> {
                _state.update {
                    it.copy(
                        serialNumber = validateAndFormatAmount(action.value.lowercase())
                    )
                }
            }

            is AddDeviceActions.OnUpdateTag -> {
                _state.update {
                    it.copy(
                        tag = action.value.lowercase()
                    )
                }
            }

            is AddDeviceActions.OnUpdatePassword -> {
                _state.update {
                    it.copy(
                        linkingPassword = action.value
                    )
                }
            }

            is AddDeviceActions.OnAddDevice -> {
                addDevice(action.callback)
            }

            is AddDeviceActions.OnBackNavigation -> {}
        }
    }

    /**
     * Add device to user's account
     *
     * @param callback callback to be called on successful add device operation
     */
    private fun addDevice(callback: (() -> Unit)?) = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        runCatching {
            // Verify credentials
            val validCredentials =
                !(state.value.serialNumber.isBlank() || state.value.linkingPassword.isBlank())

            if (!validCredentials) {
                _state.update {
                    it.copy(
                        invalidSerialNumber = state.value.serialNumber.isBlank(),
                        invalidPassword = state.value.linkingPassword.isBlank(),
                        isLoading = false,
                        error = DataError.Local.INVALID_ARGUMENT.toUiText()
                    )
                }
                return@launch
            }

            val productType = ProductType.VALUES[state.value.selectedProductIndex]

            // Request device to be added to user's account
            val result = repository.addDeviceToAccount(
                token = authenticationToken,
                productNumber = productType.productNumber,
                serialNumber = state.value.serialNumber.toLong(),
                tag = state.value.tag,
                linkingKey = state.value.linkingPassword
            )

            result
                .onSuccess {
                    _state.update {
                        it.copy(
                            isLoading = false, error = null
                        )
                    }
                    callback?.invoke()
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false, error = error.toUiText()
                        )
                    }
                }
        }.getOrElse { _ ->
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }
}